'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { RunPreviewRowsError, fetchDatasetRowsPage, fetchRunPreviewRowsPage } from '@/services/templates';
import type { DatasetRow, DatasetRowsAccessMode, DatasetRowsPage, RunPreviewRowsPage } from '@/types/templates';

export const DEFAULT_LAZY_ROWS_PAGE_SIZE = 20;

export type LazyRowsState = {
  rows: DatasetRow[];
  columns: string[];
  loadedCount: number;
  totalRows: number | null;
  hasMore: boolean;
  isLoading: boolean;
  isSyncing: boolean;
  error: string | null;
  errorCode: string | null;
  nextCursor: string | null;
  accessMode?: DatasetRowsAccessMode;
  previewCap?: number | null;
  initialized: boolean;
};

const INITIAL_STATE: LazyRowsState = {
  rows: [],
  columns: [],
  loadedCount: 0,
  totalRows: null,
  hasMore: true,
  isLoading: false,
  isSyncing: false,
  error: null,
  errorCode: null,
  nextCursor: null,
  accessMode: undefined,
  previewCap: null,
  initialized: false,
};

export function formatRowsCounterLabel(loadedCount: number, totalRows: number | null): string {
  if (totalRows == null) {
    return `Загружено строк: ${loadedCount}`;
  }
  return `Строк: ${loadedCount} / ${totalRows}`;
}

export function formatRowsLoadMoreLabel(
  loadedCount: number,
  totalRows: number | null,
  pageSize = DEFAULT_LAZY_ROWS_PAGE_SIZE,
): string {
  if (totalRows == null) {
    return `Загрузить еще ${pageSize}`;
  }
  const remainingRows = Math.max(0, totalRows - loadedCount);
  return `Загрузить еще ${Math.min(pageSize, remainingRows)}`;
}

type RowsPageLike = DatasetRowsPage | RunPreviewRowsPage;

export function appendRowsPage(previous: LazyRowsState, page: RowsPageLike): LazyRowsState {
  const pageColumns = Array.isArray(page.columns) ? page.columns : [];
  const discoveredColumns = new Set<string>([...previous.columns, ...pageColumns]);
  const accessMode = 'accessMode' in page ? page.accessMode : previous.accessMode;
  const previewCap = 'previewCap' in page ? page.previewCap : previous.previewCap;

  return {
    ...previous,
    rows: [...previous.rows, ...page.rows],
    columns: Array.from(discoveredColumns),
    loadedCount: previous.loadedCount + page.rows.length,
    totalRows: page.totalRows ?? previous.totalRows,
    hasMore: page.hasMore,
    nextCursor: page.nextCursor,
    accessMode: accessMode ?? previous.accessMode,
    previewCap: previewCap ?? previous.previewCap,
    isLoading: false,
    isSyncing: false,
    error: null,
    errorCode: null,
    initialized: true,
  };
}

type UseLazyDatasetRowsOptions = {
  datasetId?: string | null;
  enabled?: boolean;
  pageSize?: number;
  initialPage?: DatasetRowsPage | null;
};

type UseLazyRunPreviewRowsOptions = {
  runId?: string | null;
  enabled?: boolean;
  pageSize?: number;
};

type UseLazyRowsPagerOptions = {
  sourceId?: string | null;
  enabled: boolean;
  pageSize: number;
  initialPage?: RowsPageLike | null;
  fetchPage: (sourceId: string, options: { limit?: number; cursor?: string | null }) => Promise<RowsPageLike>;
};

function seedStateFromPage(page: RowsPageLike | null | undefined): LazyRowsState {
  if (!page) {
    return INITIAL_STATE;
  }

  return appendRowsPage(INITIAL_STATE, page);
}

function useLazyRowsPager(options: UseLazyRowsPagerOptions) {
  const { sourceId, enabled, pageSize, initialPage = null, fetchPage } = options;
  const [state, setState] = useState<LazyRowsState>(() => seedStateFromPage(initialPage));
  const inFlightRef = useRef<Promise<void> | null>(null);
  const datasetTokenRef = useRef(0);
  const cursorRef = useRef<string | null>(null);
  const hasMoreRef = useRef(true);
  const initializedRef = useRef(false);

  const advanceCursor = useCallback((cursor: string | null, rowsLength: number): string => {
    const offset = cursor && /^\d+$/.test(cursor) ? Number.parseInt(cursor, 10) : 0;
    return String(Math.max(0, offset) + rowsLength);
  }, []);

  const reset = useCallback(() => {
    datasetTokenRef.current += 1;
    inFlightRef.current = null;
    if (initialPage) {
      cursorRef.current = initialPage.nextCursor ?? advanceCursor(null, initialPage.rows.length);
      hasMoreRef.current = initialPage.hasMore;
      initializedRef.current = true;
      setState(seedStateFromPage(initialPage));
      return;
    }

    cursorRef.current = null;
    hasMoreRef.current = true;
    initializedRef.current = false;
    setState(INITIAL_STATE);
  }, [advanceCursor, initialPage]);

  const loadNext = useCallback(async () => {
    if (!sourceId || !enabled) {
      return;
    }
    if (initializedRef.current && !hasMoreRef.current) {
      return;
    }
    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    const token = datasetTokenRef.current;
    setState((previous) => ({ ...previous, isLoading: true, error: null, errorCode: null }));
    const cursor = cursorRef.current;

    const run = (async () => {
      try {
        const page = await fetchPage(sourceId, {
          limit: pageSize,
          cursor,
        });
        if (token !== datasetTokenRef.current) {
          return;
        }
        cursorRef.current = page.nextCursor ?? advanceCursor(cursor, page.rows.length);
        hasMoreRef.current = page.hasMore;
        initializedRef.current = true;
        setState((previous) => appendRowsPage(previous, page));
      } catch (error) {
        if (token !== datasetTokenRef.current) {
          return;
        }
        initializedRef.current = true;
        const message = error instanceof Error ? error.message : 'Failed to load rows.';
        const code = error instanceof RunPreviewRowsError ? error.code ?? null : null;
        setState((previous) => ({
          ...previous,
          isLoading: false,
          error: message,
          errorCode: code,
          initialized: true,
        }));
      } finally {
        if (token === datasetTokenRef.current) {
          inFlightRef.current = null;
        }
      }
    })();

    inFlightRef.current = run;
    return run;
  }, [advanceCursor, enabled, fetchPage, pageSize, sourceId]);

  useEffect(() => {
    reset();
    if (sourceId && enabled && !initialPage) {
      void loadNext();
    }
  }, [sourceId, enabled, initialPage, pageSize, loadNext, reset]);

  const refresh = useCallback(async () => {
    reset();
    if (sourceId && enabled) {
      await loadNext();
    }
  }, [sourceId, enabled, loadNext, reset]);

  const sync = useCallback(async () => {
    if (!sourceId || !enabled) {
      return;
    }
    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    const token = datasetTokenRef.current;
    // Background sync should not churn UI state (e.g. load-more footer) on every poll.
    // Keep the last error visible until a successful fetch clears it.
    setState((previous) => ({ ...previous, isSyncing: true }));
    const cursor = cursorRef.current;

    const run = (async () => {
      try {
        const page = await fetchPage(sourceId, {
          limit: pageSize,
          cursor,
        });
        if (token !== datasetTokenRef.current) {
          return;
        }
        cursorRef.current = page.nextCursor ?? advanceCursor(cursor, page.rows.length);
        hasMoreRef.current = page.hasMore;
        initializedRef.current = true;
        setState((previous) => appendRowsPage(previous, page));
      } catch (error) {
        if (token !== datasetTokenRef.current) {
          return;
        }
        initializedRef.current = true;
        const message = error instanceof Error ? error.message : 'Failed to load rows.';
        const code = error instanceof RunPreviewRowsError ? error.code ?? null : null;
        setState((previous) => ({
          ...previous,
          isSyncing: false,
          error: message,
          errorCode: code,
          initialized: true,
        }));
      } finally {
        if (token === datasetTokenRef.current) {
          inFlightRef.current = null;
        }
      }
    })();

    inFlightRef.current = run;
    return run;
  }, [advanceCursor, enabled, fetchPage, pageSize, sourceId]);

  return {
    ...state,
    loadNext,
    retry: loadNext,
    reset,
    refresh,
    sync,
  };
}

export function useLazyDatasetRows(options: UseLazyDatasetRowsOptions) {
  const { datasetId, enabled = true, pageSize = DEFAULT_LAZY_ROWS_PAGE_SIZE, initialPage = null } = options;
  return useLazyRowsPager({
    sourceId: datasetId,
    enabled,
    pageSize,
    initialPage,
    fetchPage: fetchDatasetRowsPage,
  });
}

export function useLazyRunPreviewRows(options: UseLazyRunPreviewRowsOptions) {
  const { runId, enabled = true, pageSize = DEFAULT_LAZY_ROWS_PAGE_SIZE } = options;
  return useLazyRowsPager({
    sourceId: runId,
    enabled,
    pageSize,
    initialPage: null,
    fetchPage: fetchRunPreviewRowsPage,
  });
}

export function useProgressiveRows<T extends DatasetRow>(
  allRows: T[],
  pageSize = DEFAULT_LAZY_ROWS_PAGE_SIZE,
) {
  const [visibleCount, setVisibleCount] = useState(pageSize);

  useEffect(() => {
    setVisibleCount(pageSize);
  }, [allRows, pageSize]);

  const loadNext = useCallback(() => {
    setVisibleCount((current) => Math.min(current + pageSize, allRows.length));
  }, [allRows.length, pageSize]);

  const visibleRows = allRows.slice(0, visibleCount);
  const hasMore = visibleCount < allRows.length;
  const columns = allRows.length
    ? Array.from(
        allRows.reduce((keys, row) => {
          Object.keys(row).forEach((key) => keys.add(key));
          return keys;
        }, new Set<string>()),
      )
    : [];

  return {
    rows: visibleRows,
    columns,
    loadedCount: visibleRows.length,
    totalRows: allRows.length,
    hasMore,
    isLoading: false,
    error: null as string | null,
    loadNext,
  };
}
